import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api from "./api";


const OAuthCallbackHandler = () => {
  const { login, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use ref to track if we've already processed this OAuth callback
  const processedCallbackRef = useRef(false);
  const isProcessingRef = useRef(false);

  // Handle OAuth callback
  useEffect(() => {
    const handleOAuthCallback = async () => {
      const searchParams = new URLSearchParams(location.search);
      const loginType = searchParams.get('login');
      
      // Check if this is an OAuth callback AND we haven't processed it yet AND user is not already authenticated
      if (loginType && 
          ['google', 'facebook', 'apple'].includes(loginType) && 
          !processedCallbackRef.current &&
          !isProcessingRef.current &&
          !isAuthenticated) {
        
        // Mark as processing immediately to prevent multiple calls
        processedCallbackRef.current = true;
        isProcessingRef.current = true;
        
        try {
          // Verify the cookie with the backend
          const response = await api.post('/auth/users/verifyCookie', {}, {
            withCredentials: true // Important to send cookies
          });

          if (response.data.status === 'success') {
            // Use the login function with the received data
            const loginSuccess = await login({
              _socialLoginResponse: response.data
            });

            if (loginSuccess) {
              toast.success(t('loginSuccess') || 'Login successful!');
              
              // Get the return URL from sessionStorage
              const returnUrl = sessionStorage.getItem('returnUrl');
              sessionStorage.removeItem('returnUrl');
              
              // Clean up the URL by removing the login parameter
              const cleanUrl = returnUrl || '/';
              navigate(cleanUrl, { replace: true });
            }
          }
        } catch (error) {
          console.error('OAuth verification error:', error);
          const message = error.response?.data?.message || t('socialLoginError') || 'Social login failed';
          toast.error(message);
          
          // Clean up the URL even on error
          navigate('/', { replace: true });
        } finally {
          isProcessingRef.current = false;
        }
      }
    };

    handleOAuthCallback();
  }, [location.search, isAuthenticated, login, navigate, t]);
  
  // Reset the processed flag when the login parameter is removed from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const loginType = searchParams.get('login');
    
    if (!loginType) {
      processedCallbackRef.current = false;
      isProcessingRef.current = false;
    }
  }, [location.search]);

  // This component doesn't render anything
  return null;
};

export default OAuthCallbackHandler;